import { setupArduinoViaHelper } from '@/features/serial/helperClient';
import { useArduinoSetupDialogStore } from '@/state/arduinoSetupDialogStore';
import { useSerialStore } from '@/state/serialStore';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';

export function ArduinoSetupDialog() {
  const open = useArduinoSetupDialogStore((state) => state.open);
  const close = useArduinoSetupDialogStore((state) => state.close);
  const refreshHelperStatus = useSerialStore((state) => state.refreshHelperStatus);

  const [installing, setInstalling] = useState(false);
  const [log, setLog] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleInstall = useCallback(async () => {
    setInstalling(true);
    setError(null);
    setLog('Installing Arduino CLI…\n');

    try {
      const result = await setupArduinoViaHelper();
      setLog((current) => current + (result.log ? `\n${result.log}` : ''));
      if (!result.success) {
        setError(result.error ?? 'Arduino setup failed.');
        return;
      }

      setDone(true);
      await refreshHelperStatus();
    } catch (installError) {
      const message =
        installError instanceof Error ? installError.message : 'Arduino setup failed.';
      setError(message);
    } finally {
      setInstalling(false);
    }
  }, [refreshHelperStatus]);

  const handleClose = () => {
    if (installing) {
      return;
    }
    close();
    setLog('');
    setError(null);
    setDone(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Install Arduino CLI</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Skyrover Helper is running, but this PC does not have Arduino CLI yet. Install it first,
          then use <strong>Board Manager</strong> to download only the board packages you need.
        </Typography>

        <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary', fontSize: 14, mb: 2 }}>
          <li>Downloads Arduino CLI into your user folder (small download)</li>
          <li>Board packages are installed separately from Board Manager</li>
          <li>Needs an internet connection</li>
        </Box>

        {installing ? <LinearProgress sx={{ mb: 2 }} /> : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {done ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Arduino CLI is ready. Open Board Manager to download board packages for your board.
          </Alert>
        ) : null}

        {log ? (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              maxHeight: 180,
              overflow: 'auto',
              bgcolor: 'grey.100',
              borderRadius: 1,
              fontSize: 12,
              fontFamily: 'Consolas, monospace',
              whiteSpace: 'pre-wrap',
            }}
          >
            {log}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={installing}>
          {done ? 'Close' : 'Later'}
        </Button>
        {!done ? (
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => void handleInstall()}
            disabled={installing}
          >
            {installing ? 'Installing…' : 'Install CLI'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
